import { UploadOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Spin, Upload } from "antd";
import { UploadProps } from "antd/es/upload";
import { FC, useState } from "react";
import { useNotification } from "~/hooks";
import { CLIENT_KEYS, useImportClients } from "~/services/client";
import { useClientStore } from "./clients.store";

export const ImportClientModal: FC = () => {
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { filters } = useClientStore();
  const importClientsMutation = useImportClients();

  const handleUpload: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
  }) => {
    if (!(file instanceof File) || file.type !== "text/csv") {
      notification.error({
        message: "Invalid file type",
        description: "Please upload a valid CSV file.",
      });
      onError?.(new Error("Invalid file type"));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      notification.error({
        message: "File too large",
        description: "The CSV file must be smaller than 10MB.",
      });
      onError?.(new Error("File too large"));
      return;
    }

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    importClientsMutation.mutate(formData, {
      onSuccess: (response) => {
        const { total, created, failed, errors } = response;
        if (created > 0) {
          notification.success({
            message: "Clients imported successfully!",
            description: `Successfully imported ${created} of ${total} clients.`,
          });
          queryClient.invalidateQueries({
            queryKey: CLIENT_KEYS.GET_CLIENTS(filters),
          });
          queryClient.invalidateQueries({
            queryKey: CLIENT_KEYS.GET_INFINITE_CLIENTS({}),
          });
          queryClient.invalidateQueries({
            queryKey: ["clientManagement/clients"],
          });
          setOpen(false);
        }
        if (failed > 0) {
          notification.warning({
            message: "Some clients failed to import",
            description: `${failed} clients could not be imported. Check the console for details.`,
          });
          console.error("Import errors:", errors);
        }
        onSuccess?.({});
      },
      onError: (err: any) => {
        notification.error({
          message: "Error importing clients",
          description:
            err.message || "There was an error processing the CSV file.",
        });
        onError?.(err);
      },
      onSettled: () => {
        setIsUploading(false);
      },
    });
  };

  return (
    <>
      <Button
        size="large"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm flex items-center space-x-2"
        onClick={() => setOpen(true)}
        disabled={isUploading}
      >
        <UploadOutlined />
        <span>Import Client CSV</span>
      </Button>
      <Modal
        open={open}
        title="Import Clients from CSV"
        onCancel={() => setOpen(false)}
        footer={null}
      >
        <Spin spinning={isUploading}>
          <Upload
            accept=".csv"
            customRequest={handleUpload}
            showUploadList={false}
            disabled={isUploading}
          >
            <Button icon={<UploadOutlined />} disabled={isUploading}>
              Click to Upload CSV
            </Button>
          </Upload>
          <div className="mt-4 text-sm text-gray-500">
            <p>Please upload a CSV file with the correct format.</p>
            <p>Maximum file size: 10MB.</p>
          </div>
        </Spin>
      </Modal>
    </>
  );
};
